import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { getSupabaseModule } from '../lib/lazySupabase';
import { isPlaceholderEmail } from '../lib/golferEmail';
import {
  completeProSignupAfterAuth,
  proFieldsFromUserMetadata,
} from '../lib/completeProSignup';

type SupabaseClient = Awaited<ReturnType<typeof getSupabaseModule>>['supabase'];

/** Mirror Core's auth bootstrap timeout — PWA reopen with a hung
 *  getSession left ProtectedRoute on GolfAuthLoadingScreen forever. */
const AUTH_NETWORK_TIMEOUT_MS = 6000;

class AuthBootstrapTimeoutError extends Error {
  constructor(label: string) {
    super(`auth bootstrap timeout: ${label}`);
    this.name = 'AuthBootstrapTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new AuthBootstrapTimeoutError(label)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** True when a PostgREST/Supabase error means the JWT was missing/rejected
 *  (anon column grants → 401, clock skew → PGRST303, etc.). Callers must
 *  NOT treat this as "row not found". */
function isAuthRestFailure(
  error: { code?: string; message?: string; status?: number } | null | undefined,
  httpStatus?: number | null,
): boolean {
  if (httpStatus === 401 || httpStatus === 403) return true;
  if (!error) return false;
  const status = typeof error.status === 'number' ? error.status : undefined;
  const code = (error.code ?? '').toUpperCase();
  const msg = (error.message ?? '').toLowerCase();
  if (status === 401 || status === 403) return true;
  if (code === 'PGRST303' || code === '42501') return true;
  if (/jwt|unauthorized|permission denied|not authorized/i.test(msg)) return true;
  return false;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Prevent overlapping initializeAuth from visibility + cold start. */
let initializeAuthInFlight: Promise<void> | null = null;

interface ProfessionalProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company?: string;
  facility?: string;
  certification_number?: string;
  certifications?: string[];
  address?: string;
  postcode?: string;
  booking_url?: string;
  brands?: string[];
  rating?: number;
  reviews?: number;
  image?: string;
  subscription_status?: 'trial' | 'grace_period' | 'active' | 'locked' | 'canceled';
  subscription_grace_period_ends_at?: string;
  tier?: string;
  /** Admin approval lifecycle, separate from billing. Pending = newly
   *  signed up, awaiting Mission Control review. Approved = OK to use
   *  Fitting. Suspended = temporarily blocked. Blocked = permanently. */
  approval_status?: 'pending' | 'approved' | 'suspended' | 'blocked';
  approval_status_note?: string | null;
}

interface CustomerProfile {
  id: string;
  email: string;
  email_verified?: boolean;
  first_name: string;
  last_name: string;
  phone?: string;
  handicap?: number;
  swing_speed?: number;
  height?: string;
  wrist_to_floor?: string;
  playing_frequency?: string;
  preferred_ball_flight?: string;
  notes?: string;
  professional_id?: string;
  auth_id?: string;
  /** 6-char code shared with a professional to grant profile access. Already
   * selected via `select('*')` in every auth query — this just makes it
   * visible on the typed surface so the Navbar can render it. */
  lookup_code?: string | null;
}

interface AuthState {
  professional: ProfessionalProfile | null;
  customer: CustomerProfile | null;
  isInitialized: boolean;
  isLoading: boolean;
  login: (email: string, password: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  customerLogin: (email: string, password: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  /** Refetch customer from DB (e.g. after profile update) so Navbar shows current name */
  refreshCustomer: () => Promise<void>;
}

async function linkExistingCustomerByEmail(
  supabase: SupabaseClient,
  authId: string,
  email: string,
  emailLower: string,
  insertError: { code?: string; message?: string },
): Promise<CustomerProfile | null> {
  if (insertError.code !== '23505' || !insertError.message?.includes('customers_email')) {
    return null;
  }

  let existing = (
    await supabase.from('customers').select('*').eq('email_lower', emailLower).maybeSingle()
  ).data;

  if (!existing) {
    existing = (
      await supabase.from('customers').select('*').ilike('email', email).maybeSingle()
    ).data;
  }

  if (!existing) return null;

  const { data: linked, error: linkError } = await supabase
    .from('customers')
    .update({ auth_id: authId })
    .eq('id', existing.id)
    .select()
    .single();

  if (linkError) {
    console.error('[authStore.linkExistingCustomerByEmail] link failed', {
      code: linkError.code,
      message: linkError.message,
      customerId: existing.id,
      authId,
    });
    return null;
  }

  return linked as CustomerProfile;
}

/** Claim an unlinked (or already-self) customers row by phone. */
async function claimCustomerByPhone(
  supabase: SupabaseClient,
  authId: string,
  sessionPhone: string,
): Promise<
  | { kind: 'customer'; customer: CustomerProfile }
  | { kind: 'auth_failure' }
  | { kind: 'none' }
> {
  if (!sessionPhone) return { kind: 'none' };
  const phoneE164 = sessionPhone.startsWith('+') ? sessionPhone : `+${sessionPhone}`;

  const {
    data: row,
    error,
    status,
  } = await supabase.from('customers').select('*').eq('phone', phoneE164).maybeSingle();

  if (isAuthRestFailure(error, status)) return { kind: 'auth_failure' };
  if (!row) return { kind: 'none' };

  // Do not steal another auth user's customer.
  if (row.auth_id && row.auth_id !== authId) return { kind: 'none' };

  if (row.auth_id === authId) {
    return { kind: 'customer', customer: row as CustomerProfile };
  }

  const {
    data: linked,
    error: linkError,
    status: linkStatus,
  } = await supabase
    .from('customers')
    .update({ auth_id: authId })
    .eq('id', row.id)
    .select()
    .single();

  if (isAuthRestFailure(linkError, linkStatus)) return { kind: 'auth_failure' };
  if (!linked) return { kind: 'none' };
  return { kind: 'customer', customer: linked as CustomerProfile };
}

type AuthUserLite = {
  id: string;
  email?: string | null;
  phone?: string | null;
  email_confirmed_at?: string | null;
  user_metadata?: Record<string, unknown> | null;
};

/**
 * Insert (or unique-conflict link) a customers row for an authenticated golfer.
 * Used when auth + profiles exist but customers was deleted — Fitting login
 * requires customers even though Core only needs profiles.
 */
async function ensureCustomerRowForAuthUser(
  supabase: SupabaseClient,
  user: AuthUserLite,
  profileData: {
    name?: string | null;
    phone?: string | null;
    handicap?: number | null;
  } | null,
): Promise<
  | { kind: 'customer'; customer: CustomerProfile }
  | { kind: 'auth_failure' }
  | { kind: 'none' }
> {
  const metaName =
    typeof user.user_metadata?.name === 'string' ? user.user_metadata.name : '';
  const name = (profileData?.name || metaName || '').trim();
  const nameParts = name.split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || 'Golfer';
  const lastName = nameParts.slice(1).join(' ') || '';

  const email =
    user.email ||
    (user.phone ? `${user.phone}@phone.golfauth.local` : `phone-${user.id}@phone.golfauth.local`);
  const emailLower = email.toLowerCase();
  const emailVerified =
    !emailLower.endsWith('@phone.golfauth.local') && !!user.email_confirmed_at;

  const {
    data: createdCustomer,
    error: createError,
    status: createStatus,
  } = await supabase
    .from('customers')
    .insert({
      auth_id: user.id,
      email,
      email_lower: emailLower,
      email_verified: emailVerified,
      first_name: firstName,
      last_name: lastName,
      phone: profileData?.phone || user.phone || null,
      handicap: profileData?.handicap ?? null,
      professional_id: null,
    })
    .select()
    .single();

  if (!createError && createdCustomer) {
    return { kind: 'customer', customer: createdCustomer as CustomerProfile };
  }

  if (createError) {
    if (isAuthRestFailure(createError, createStatus)) return { kind: 'auth_failure' };
    console.error('[authStore.initializeAuth] customers insert self-heal failed', {
      code: createError.code,
      message: createError.message,
      authId: user.id,
      email,
    });
    const linked = await linkExistingCustomerByEmail(
      supabase,
      user.id,
      email,
      emailLower,
      createError,
    );
    if (linked) return { kind: 'customer', customer: linked };

    const phoneClaim = await claimCustomerByPhone(supabase, user.id, user.phone || '');
    if (phoneClaim.kind === 'auth_failure') return { kind: 'auth_failure' };
    if (phoneClaim.kind === 'customer') return phoneClaim;
  }

  return { kind: 'none' };
}

export const useAuthStore = create<AuthState>((set) => ({
  professional: null,
  customer: null,
  isInitialized: false,
  isLoading: false,

  login: async (email: string, password: string, captchaToken?: string) => {
    set({ isLoading: true });
    try {
      const { supabase } = await getSupabaseModule();
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();

      // Sign in with Supabase Auth. Supabase's captcha gate is
      // enforced on all auth mutations once the Dashboard toggle is
      // on, not just signup — the token is required here too or the
      // POST returns 400 captcha_failed.
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (authError) {
        set({ isLoading: false });
        let errorMessage = 'An unexpected error occurred';

        if (authError.message.includes('Invalid login credentials')) {
          errorMessage = 'invalid_credentials';
        } else if (authError.message.includes('Email not confirmed')) {
          errorMessage = 'email_not_confirmed';
        } else {
          errorMessage = authError.message;
        }

        return { success: false, error: errorMessage };
      }

      if (!authData.user) {
        set({ isLoading: false });
        return { success: false, error: 'No user data returned' };
      }

      // Fetch professional profile using the authenticated user's ID
      const { data: professionalData, error: profileError } = await supabase
        .from('professionals')
        .select('*')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError) {
        set({ isLoading: false });
        return { success: false, error: profileError.message };
      }

      if (!professionalData) {
        // No professionals row — could be three cases:
        //   1. This user is genuinely a golfer trying to use Pro login (legit error)
        //   2. They registered as a pro but the immediate post-signUp insert
        //      from RegisterPage failed with 401 (race with session establishment)
        //      AND the DB trigger that backfills it didn't fire on this Supabase
        //      project (some hosted setups restrict auth-schema triggers)
        //   3. Their professional row was manually deleted
        //
        // For case 2, recover here: user_metadata.user_type='professional'
        // marks the signup intent at signUp time and survives across sessions.
        // We have a live session now, so the insert WILL succeed under RLS.
        const meta = authData.user.user_metadata ?? {};
        // Only recover after email is confirmed — otherwise login would
        // put unverified applicants into the Mission Control queue.
        if (
          meta.user_type === 'professional' &&
          authData.user.email_confirmed_at
        ) {
          try {
            // Insert pending row + fire under-review / prosign@ emails
            // (idempotent). Covers orphans from the immediate-session
            // signup bug where Auth existed without professionals.
            await completeProSignupAfterAuth(
              authData.user.id,
              proFieldsFromUserMetadata(authData.user),
            );
            const { data: created } = await supabase
              .from('professionals')
              .select('*')
              .eq('id', authData.user.id)
              .maybeSingle();
            if (created) {
              set({ professional: created, customer: null, isLoading: false, isInitialized: true });
              return { success: true };
            }
          } catch (insertErr) {
            console.error('[authStore] Pro recovery insert failed:', insertErr);
          }
        }
        // Case 1 or 3 — genuinely no pro profile.
        set({ isLoading: false });
        return { success: false, error: 'No professional profile found for this account. Please check if you should be using the Golfer Sign In instead.' };
      }

      // Update state with professional data
      set({ professional: professionalData, customer: null, isLoading: false, isInitialized: true });

      return { success: true };
    } catch (error) {
      set({ isLoading: false });
      let errorMessage = 'An unexpected error occurred';

      if (error instanceof Error) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'invalid_credentials';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'email_not_confirmed';
        } else if (error.message.includes('No professional profile found')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }

      return { success: false, error: errorMessage };
    }
  },

  customerLogin: async (email: string, password: string, captchaToken?: string) => {
    set({ isLoading: true });
    try {
      const { supabase } = await getSupabaseModule();
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();

      // First, authenticate the user. Captcha gate — see login() above.
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (authError) {
        set({ isLoading: false });
        let errorMessage = 'An unexpected error occurred';

        if (authError.message.includes('Invalid login credentials')) {
          errorMessage = 'invalid_credentials';
        } else if (authError.message.includes('Email not confirmed')) {
          errorMessage = 'email_not_confirmed';
        } else if (authError.message.includes('Database error granting user')) {
          errorMessage = 'There was a temporary issue with the authentication system. Please try again in a moment.';
        } else {
          errorMessage = authError.message;
        }

        return { success: false, error: errorMessage };
      }

      if (!authData.user) {
        set({ isLoading: false });
        return { success: false, error: 'No user data returned' };
      }

      // Check for existing customer profile by auth_id first
      const { data: customerByAuthId, error: authIdLookupError } = await supabase
        .from('customers')
        .select('*')
        .eq('auth_id', authData.user.id)
        .maybeSingle();

      if (authIdLookupError) {
        set({ isLoading: false });
        return { success: false, error: 'Unable to retrieve customer profile. Please try again.' };
      }

      if (customerByAuthId) {
        // Customer profile already exists and is properly linked
        set({ customer: customerByAuthId, professional: null, isLoading: false, isInitialized: true });
        return { success: true };
      }

      // No customer profile found by auth_id, check if one exists by email (case-insensitive)
      const { data: customerByEmail, error: emailLookupError } = await supabase
        .from('customers')
        .select('*')
        .eq('email_lower', normalizedEmail)
        .maybeSingle();

      if (emailLookupError) {
        set({ isLoading: false });
        return { success: false, error: 'Unable to retrieve customer profile. Please try again.' };
      }

      if (customerByEmail) {
        // Customer profile exists but isn't linked to this auth_id
        // Update the existing profile to link it to the authenticated user
        const { data: updatedCustomer, error: updateError } = await supabase
          .from('customers')
          .update({ auth_id: authData.user.id })
          .eq('id', customerByEmail.id)
          .select()
          .single();

        if (updateError) {
          set({ isLoading: false });
          return { success: false, error: 'Unable to link customer profile. Please contact support.' };
        }

        set({ customer: updatedCustomer, professional: null, isLoading: false, isInitialized: true });
        return { success: true };
      }

      // No customer profile exists at all, create a new one
      // Extract name from user metadata if available
      const firstName = authData.user.user_metadata?.first_name ||
                       authData.user.user_metadata?.full_name?.split(' ')[0] ||
                       '';
      const lastName = authData.user.user_metadata?.last_name ||
                      authData.user.user_metadata?.full_name?.split(' ').slice(1).join(' ') ||
                      '';

      const emailVerified =
        !isPlaceholderEmail(normalizedEmail) && !!authData.user.email_confirmed_at;
      const newCustomer = {
        auth_id: authData.user.id,
        email: normalizedEmail,
        email_lower: normalizedEmail,
        email_verified: emailVerified,
        first_name: firstName,
        last_name: lastName,
      };

      const { data: createdCustomer, error: createError } = await supabase
        .from('customers')
        .insert(newCustomer)
        .select()
        .single();

      if (createError) {
        // Handle the specific case of duplicate email constraint violation
        if (createError.code === '23505' && createError.message.includes('customers_email')) {
          // A customer with this email was created between our check and insert
          // Try to fetch and link the existing customer
          const { data: existingCustomer, error: fetchError } = await supabase
            .from('customers')
            .select('*')
            .eq('email_lower', normalizedEmail)
            .maybeSingle();

          if (fetchError) {
            return { success: false, error: 'Unable to retrieve existing customer profile. Please try again.' };
          }

          if (existingCustomer) {
            // Update the existing customer to link it to this auth user
            const { data: linkedCustomer, error: linkError } = await supabase
              .from('customers')
              .update({ auth_id: authData.user.id })
              .eq('id', existingCustomer.id)
              .select()
              .single();

            if (linkError) {
              set({ isLoading: false });
              return { success: false, error: 'Unable to link existing customer profile. Please contact support.' };
            }

            set({ customer: linkedCustomer, professional: null, isLoading: false, isInitialized: true });
            return { success: true };
          }
        }

        set({ isLoading: false });
        return { success: false, error: 'Unable to create customer profile. Please try again or contact support.' };
      }

      // Set customer state with the newly created profile
      set({ customer: createdCustomer, professional: null, isLoading: false, isInitialized: true });
      return { success: true };

    } catch (error) {
      set({ isLoading: false });
      let errorMessage = 'An unexpected error occurred';

      if (error instanceof Error) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'invalid_credentials';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'email_not_confirmed';
        } else if (error.message.includes('Database error granting user')) {
          errorMessage = 'There was a temporary issue with the authentication system. Please try again in a moment.';
        } else if (error.message.includes('duplicate key value')) {
          errorMessage = 'account_exists';
        } else {
          errorMessage = error.message;
        }
      }

      return { success: false, error: errorMessage };
    }
  },

  logout: async () => {
    set({ isLoading: true });
    const { supabase } = await getSupabaseModule();
    const { error } = await supabase.auth.signOut();
    if (error) {
      set({ isLoading: false });
      throw error;
    }
    set({ professional: null, customer: null, isLoading: false });
  },

  refreshCustomer: async () => {
    const { supabase } = await getSupabaseModule();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const { data: customer } = await supabase
      .from('customers')
      .select('*')
      .eq('auth_id', session.user.id)
      .maybeSingle();
    if (customer) {
      set((s) => ({ ...s, customer }));
    }
  },

  initializeAuth: async () => {
    if (initializeAuthInFlight) return initializeAuthInFlight;
    initializeAuthInFlight = (async () => {
    try {
      set({ isLoading: true });
      const { supabase } = await getSupabaseModule();
      let session: Awaited<
        ReturnType<typeof supabase.auth.getSession>
      >['data']['session'] = null;
      try {
        const result = await withTimeout(
          supabase.auth.getSession(),
          AUTH_NETWORK_TIMEOUT_MS,
          'getSession',
        );
        session = result.data.session;
      } catch (err) {
        if (err instanceof AuthBootstrapTimeoutError) {
          console.warn(
            '[authStore] getSession timed out — leaving uninitialized for resume retry',
          );
          // Keep isInitialized=false so ProtectedRoute stays on the
          // loading screen AND visibility/online handlers in
          // AuthStateHandler can call initializeAuth again. Flipping
          // initialized=true with null customer would bounce signed-in
          // PWA users to login.
          set({ isLoading: false });
          return;
        }
        throw err;
      }

      if (!session?.user) {
        set({ professional: null, customer: null, isInitialized: true, isLoading: false });
        return;
      }

      const loadProfilesForSession = async (
        user: NonNullable<typeof session>['user'],
      ): Promise<'done' | 'auth_failure'> => {
        // Try to load professional profile first
        const {
          data: professionalData,
          error: proError,
          status: proStatus,
        } = await supabase
          .from('professionals')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (isAuthRestFailure(proError, proStatus)) return 'auth_failure';

        if (professionalData) {
          set({ professional: professionalData, customer: null, isInitialized: true, isLoading: false });
          return 'done';
        }

        // If not a professional, try to load customer profile by auth_id
        const {
          data: customerByAuthId,
          error: customerAuthError,
          status: customerAuthStatus,
        } = await supabase
          .from('customers')
          .select('*')
          .eq('auth_id', user.id)
          .maybeSingle();

        if (isAuthRestFailure(customerAuthError, customerAuthStatus)) return 'auth_failure';

        if (customerByAuthId) {
          // Heal email_verified when Auth already confirmed (Core email OTP)
          // but the customers row was created without the flag.
          let customer = customerByAuthId;
          const authEmail = (user.email ?? '').toLowerCase();
          const authConfirmed =
            !!user.email_confirmed_at &&
            !!authEmail &&
            !authEmail.endsWith('@phone.golfauth.local');
          if (authConfirmed && customer.email_verified !== true) {
            const { data: healed } = await supabase
              .from('customers')
              .update({ email_verified: true })
              .eq('id', customer.id)
              .select()
              .single();
            if (healed) customer = healed;
          }
          set({ professional: null, customer, isInitialized: true, isLoading: false });
          return 'done';
        }

        // No customer by auth_id, try by email and link if found
        const normalizedEmail = user.email?.toLowerCase().trim() || '';
        if (normalizedEmail) {
          const {
            data: customerByEmail,
            error: emailLookupError,
            status: emailLookupStatus,
          } = await supabase
            .from('customers')
            .select('*')
            .eq('email_lower', normalizedEmail)
            .maybeSingle();

          if (isAuthRestFailure(emailLookupError, emailLookupStatus)) return 'auth_failure';

          if (customerByEmail) {
            // Link the auth_id to the existing customer
            const {
              data: linkedCustomer,
              error: linkError,
              status: linkStatus,
            } = await supabase
              .from('customers')
              .update({ auth_id: user.id })
              .eq('id', customerByEmail.id)
              .select()
              .single();

            if (isAuthRestFailure(linkError, linkStatus)) return 'auth_failure';

            if (linkedCustomer) {
              set({ professional: null, customer: linkedCustomer, isInitialized: true, isLoading: false });
              return 'done';
            }
          }
        }

        // Still no match — try by phone (unlinked or already this auth user).
        const sessionPhone = user.phone || '';
        if (sessionPhone) {
          const phoneClaim = await claimCustomerByPhone(supabase, user.id, sessionPhone);
          if (phoneClaim.kind === 'auth_failure') return 'auth_failure';
          if (phoneClaim.kind === 'customer') {
            set({
              professional: null,
              customer: phoneClaim.customer,
              isInitialized: true,
              isLoading: false,
            });
            return 'done';
          }
        }

        // No customer found — check Core profiles (golfer from Core / store-review).
        const {
          data: profileData,
          error: profileError,
          status: profileStatus,
        } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (isAuthRestFailure(profileError, profileStatus)) return 'auth_failure';

        // Self-heal customers when auth exists. Fitting CustomerLogin requires
        // a customers row; deleting it (while leaving auth/profiles) previously
        // left reviewers stuck on "could not load your golfer profile".
        // Also heal phone-auth golfers with no profiles row yet.
        if (profileData || user.phone || user.email) {
          const ensured = await ensureCustomerRowForAuthUser(
            supabase,
            user,
            profileData
              ? {
                  name: profileData.name,
                  phone: profileData.phone,
                  handicap: profileData.handicap,
                }
              : null,
          );
          if (ensured.kind === 'auth_failure') return 'auth_failure';
          if (ensured.kind === 'customer') {
            set({
              professional: null,
              customer: ensured.customer,
              isInitialized: true,
              isLoading: false,
            });
            return 'done';
          }
        }

        // Authenticated but no customer/profile/professional found.
        // Leave customer null — CustomerRegister's complete-golfer-signup
        // owns creation for brand-new golfers.
        set({ professional: null, customer: null, isInitialized: true, isLoading: false });
        return 'done';
      };

      let outcome = await loadProfilesForSession(session.user);
      if (outcome === 'auth_failure') {
        // Session exists in Auth but PostgREST rejected the JWT (stale /
        // truncated cookie, clock skew, anon fallback). Refresh once and
        // retry — do NOT report "no golfer profile".
        console.warn('[authStore.initializeAuth] profile REST auth failure — refreshing session and retrying');
        try {
          await supabase.auth.refreshSession();
        } catch (refreshErr) {
          console.warn('[authStore.initializeAuth] refreshSession failed', refreshErr);
        }
        await delay(400);
        const {
          data: { session: retrySession },
        } = await supabase.auth.getSession();
        if (retrySession?.user) {
          outcome = await loadProfilesForSession(retrySession.user);
        }
        if (outcome === 'auth_failure') {
          console.error('[authStore.initializeAuth] profile load still unauthorized after refresh');
          // Keep isInitialized true so the login UI can show a retryable
          // error (CustomerLogin checks session vs customer).
          set({ professional: null, customer: null, isInitialized: true, isLoading: false });
        }
      }
    } catch (error) {
      set({ professional: null, customer: null, isInitialized: true, isLoading: false });
    }
    })();
    try {
      await initializeAuthInFlight;
    } finally {
      initializeAuthInFlight = null;
    }
  },
}));

// Module-scope guard so AuthStateHandler can mount/unmount safely without
// double-registering the onAuthStateChange listener.
let installed = false;

/**
 * Defers the Supabase SDK + auth listener to first call. AuthStateHandler
 * invokes this in a useEffect after first paint so the marketing homepage
 * doesn't block on auth.
 *
 * Idempotent: subsequent calls are no-ops and return the existing
 * unsubscribe function (or undefined if SSR).
 *
 * On first call:
 *   1. Dynamically loads `lib/supabase` (incl. SDK + client construction).
 *   2. Awaits `supabaseAuthReady` so any Core/Capacitor URL-token handoff
 *      has completed before we wire up state listeners. Otherwise the
 *      first INITIAL_SESSION event fires with session=null and the
 *      auth-store handler flips isInitialized=true with no customer —
 *      causing GolferProtectedRoute to bounce a freshly handed-off user
 *      to /customer/login before the session lands.
 *   3. Kicks off `initializeAuth()` to hydrate professional / customer.
 *   4. Registers `onAuthStateChange` to keep Zustand state + Sentry user
 *      context in sync.
 *   5. Wires a `beforeunload` listener to clean up the subscription.
 */
export async function installAuthListener(): Promise<(() => void) | undefined> {
  if (typeof window === 'undefined') return undefined;
  if (installed) return undefined;
  installed = true;

  const { supabase, supabaseAuthReady } = await getSupabaseModule();

  // Await the URL-token handoff (Core/Capacitor cross-WebView nav writes
  // ?token=… &refresh_token=… into the URL, supabase.ts strips them and
  // calls setSessionFromToken) BEFORE wiring onAuthStateChange. supabase-js
  // fires INITIAL_SESSION the moment the listener registers, reflecting
  // whatever state the SDK has right now — and if we register while
  // setSessionFromToken is still in flight, that first event arrives with
  // session=null, the handler below sets isInitialized=true + customer=null,
  // and GolferProtectedRoute redirects to /customer/login before the
  // session ever lands. For direct visits without URL tokens the
  // supabaseAuthReady early-returns synchronously, so this adds no
  // perceptible delay — the wait only kicks in on the Capacitor handoff
  // path that was previously broken on fresh-install iOS bundles.
  await supabaseAuthReady;

  void useAuthStore.getState().initializeAuth();

  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    // Wire Sentry user context for every auth transition. Email is scrubbed
    // by the existing beforeSend hook in sentry.ts before any payload leaves
    // the browser — Sentry only stores the UUID for issue correlation.
    // Without this, every Sentry event lands as user:null, making it
    // impossible to tell which specific user hit an error.
    if (session?.user) {
      Sentry.setUser({ id: session.user.id, email: session.user.email });
    } else {
      Sentry.setUser(null);
    }
    (async () => {
      if (event === 'SIGNED_OUT' || !session) {
        useAuthStore.setState({
          professional: null,
          customer: null,
          isInitialized: true,
          isLoading: false
        });
      } else if (event === 'SIGNED_IN') {
        const state = useAuthStore.getState();
        if (!state.professional && !state.customer) {
          await useAuthStore.getState().initializeAuth();
        }
      } else if (event === 'TOKEN_REFRESHED') {
        const state = useAuthStore.getState();
        if (!state.professional && !state.customer) {
          await useAuthStore.getState().initializeAuth();
        }
      }
    })();
  });

  const unsubscribe = () => subscription.unsubscribe();

  window.addEventListener('beforeunload', unsubscribe);

  return unsubscribe;
}
